import { Box, CircularProgress, Divider, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { noop } from 'lodash';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import * as React from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { protectedApiCall } from '../../api/network';
import { INTERVIEW_ASSEMBLY_TYPES, SUPPORTED_LANGUAGES } from '../../constants';
import { baseAtom, downloadExportInterviewSignedURL, getFirstInterview } from '../../stores/jotai/base';
import { discoverySessionAtom } from '../../stores/jotai/discovery-session';
import { interviewsAtom } from '../../stores/jotai/interviews/interviews';
import { hasPermissionCanvasWrite } from '../../stores/jotai/permissions';
import { reTranscriptionProgressAtom, setRetrancriptionProgressByInterviewID } from '../../stores/jotai/transcript/reTranscriptionProgress';
import { useDeleteInterview } from '../../tanstackQuery/queries/interviews';
import { useExportInterview } from '../../tanstackQuery/queries/interviews/useExportInterview';
import { appLog } from '../../util';
import AlertDialog from '../app/alert-dialog';
import IwButton from '../app/IwButton';
import { DropdownV2 } from '../assorted/dropdown-v2';
import { renderErrorToast } from '../../../lib/ErrorToastMessage';

export default function DiscoveryCompactMenuItem(props) {
    const { menuSide='left', menuSx={}, artifacts } = props;
    const { notes, transcript, video, media } = artifacts

    const [discoverySession, setDiscoverySession] = useAtom(discoverySessionAtom);
    const setRetranscriptionProgress = useSetAtom(reTranscriptionProgressAtom);
    const [base, setBase] = useAtom(baseAtom);
    const [action, setAction] = useState("");
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [alertModalTitle, setAlertModalTitle] = useState("");
    const [alertModalDescription, setAlertModalDescription] = useState("");
    const [isEnableInterview,setIsEnableInterview]= useState(false)
    const [selectedPopup, setSelectedPopup] = useState("");
    
    const [isAlertModalLoading, setIsAlertModalLoading] = useState(false);
    const { keycloak } = useKeycloak();
    const [allInterview,]=useAtom(interviewsAtom);
    const [language, setLanguage] = useState<string>("English");

    const client = useQueryClient();
    const { mutateAsync: handleDeleteInterview } = useDeleteInterview(
      { accessToken: keycloak?.token },
      {
        onSuccess() {
          client.invalidateQueries({ queryKey: ["interviews"] });
          client.invalidateQueries({ queryKey: ["artifacts"] });
        }
      }
    );
    const { isLoading:isInterviewDownloadLoading, refetch } = useExportInterview(
        { accessToken: keycloak?.token },
        { interviewID:props.interviewID },
        { enabled: isEnableInterview } 
    );
   
    const selectedInterviewDetails = allInterview?.find(
      (interview) => interview?.interviewID === base.selectedInterview
    );

    const theme = useTheme();
    const canEditCanvas = hasPermissionCanvasWrite(base);

    const menuItemLoader = (
        <CircularProgress
            size={16}
            sx={{ marginLeft: "auto" }}
        />
    );

    const optionStyles = {
        gap: "16px",
        backgroundColor: 'white',
        ':hover': {
            backgroundColor: theme.custom.hoverOptionColor
        }
    }

    const handleExport = (event, menuProps: any, type: string) => {
        event.stopPropagation();

        const interviewID = props.interviewID;

        protectedApiCall(discoverySession, "export/interview", {

        }, {
            params: {
                interviewID,
                type: type
            }
        }, "get").then((r) => {
            //@ts-ignore
            if (r !== undefined && r.exportURL !== undefined) {
                //@ts-ignore
                const exportURL = r.exportURL;

                window.open(exportURL, '_blank')

                menuProps.onClose(event);

                setAction("");
            }
            else {
                appLog("error export interview data");

                setAction("");
            }
        })
    }

    const handleExportMedia = (event, menuProps: any) => {
        setAction('exportMedia');
        handleExport(event, menuProps, 'audio');
    }

    const handleExportNotes = (event, menuProps: any) => {
        setAction('exportNotes');
        handleExport(event, menuProps, 'notes');
    }
    const handleExportInterviews = async () => {
      if (
        transcript === undefined &&
        video === undefined &&
        media === undefined && 
        notes === undefined
      ) {
        return renderErrorToast("You do not have any notes or transcription!")
      }
      
        if (isInterviewDownloadLoading) { // Prevent duplicate or overlapping downloads
            return;
        }
        setIsEnableInterview(true);      
    
        try {
            const {data: InterviewDownloadData} = await refetch(); 
            if (InterviewDownloadData) {
                downloadExportInterviewSignedURL(InterviewDownloadData?.signedUrl,`${base?.team?.name}_${props?.interviewee}`);
            } else {
              renderErrorToast("Export failed: try again later")
            }
        } catch (error) {
          renderErrorToast("Export failed: try again later")
        } finally {
            setIsEnableInterview(false);
        }
    };
    

  const handleConfirmReTranscribe = (event, menuProps: any, interviewID: string) => {
      setAlertModalTitle("Re-Transcribe Media");
      setAlertModalDescription("Generating a new transcription will permanently delete your current insights with no way to recover them.Are you sure you want to proceed?");
      menuProps.onClose(event);
      setIsAlertModalOpen(true);
      setSelectedPopup("reTranscribe");
      event.stopPropagation();
  }

  const handleGenerateAnalysis =(event, menuProps: any, interviewID: string)=>{
      setAlertModalTitle("Generate Meta-analysis");
      setAlertModalDescription("Generate Meta Analysis to get comprehensive insights and thematic analysis from your interview transcripts, automatically mapped to your Business Model Canvas.");
      menuProps.onClose(event);
      setIsAlertModalOpen(true);
      setSelectedPopup("generateAnalysis");
      event.stopPropagation();
  }

  const handleConfirmDeleteMedia = (event, menuProps: any, interviewID: string) => {
    setAlertModalTitle("Delete Media");
    setAlertModalDescription("Are you sure you want to delete media ? It will delete your transcription including Insights.");
    menuProps.onClose(event);
    setIsAlertModalOpen(true);
    setSelectedPopup("deleteMedia");
    event.stopPropagation();
  }

    const handleReTranscribe = () => {
      if (
        transcript === undefined &&
        video === undefined &&
        media === undefined
      ) {
        return renderErrorToast("You do not have any transcription!")
      }
      setLanguage("English");
      const { interviewID } = props;
      setAction("transcribe");
      setIsAlertModalLoading(true);

      protectedApiCall(
        discoverySession,
        "interview/retranscribe",
        {
          interviewID,
          language
        },
        {},
        "put"
      )
        .then((res) => {
          setRetrancriptionProgressByInterviewID(
            setRetranscriptionProgress,
            interviewID,
            true
          );
          client.invalidateQueries({ queryKey: ["artifacts"] });
          client.invalidateQueries({ queryKey: ["meta-artifacts"] });
          setAction("");
        })
        .finally(() => {
          setIsAlertModalLoading(false);
          setIsAlertModalOpen(false);
        });
    };

    const handleDeleteTranscript = () => {
        const { interviewID } = props;
        if (
          transcript === undefined &&
          video === undefined &&
          media === undefined
        ) {
          return renderErrorToast("You do not have any transcription!")
        }
        
        setAction("delete-transcript");
        setIsAlertModalLoading(true);

        protectedApiCall(
            discoverySession,
            "refactor/transcript",
            {
                interviewID,
            },
            {},
            "delete"
        )
            .then(() => {
                client.invalidateQueries({ queryKey: ["assemblies"] });
                setAction("");
                client.invalidateQueries({ queryKey: ["artifacts"] });
                client.invalidateQueries({ queryKey: ["meta-artifacts"] });
            })
            .finally(() => {
                setIsAlertModalOpen(false);
                setIsAlertModalLoading(false);
            });
    };

    const handleDeleteMedia = () => {
      if (
        transcript === undefined &&
        video === undefined &&
        media === undefined
      ) {
        return renderErrorToast("You do not have any media!")
      }
      const { interviewID, artifacts } = props;
      setAction("delete-media");
      setIsAlertModalLoading(true);

      const apiParams = {
        interviewID,
        teamID: base.team.groupID
      };

      protectedApiCall(
        discoverySession,
        "interview/transcript-media",
        {},
        { params: apiParams,},
        "delete"
      )
        .then(() => {
          client.invalidateQueries({ queryKey: ["assemblies"] });
          client.invalidateQueries({ queryKey: ["notifications"] });
          client.invalidateQueries({ queryKey: ["artifacts"] });
          client.invalidateQueries({ queryKey: ["insights"] });
          client.invalidateQueries({ queryKey: ["meta-artifacts"] });
          setAction("");
        })
        .finally(() => {
          setIsAlertModalLoading(false);
          setIsAlertModalOpen(false);
        });
    };

    const HandleGenerateAnalysis=()=>{
      const { interviewID, artifacts } = props;

      if (
        (transcript === undefined && 
        video === undefined &&
        media === undefined) || (selectedInterviewDetails?.isMetaInsightGenerated) 
      ) {
        return renderErrorToast("You do not have any media!")
      }
      
      setAction("generate-analysis");
      setIsAlertModalLoading(true);

      const apiParams = {
        interviewID,
        groupID: base.team.groupID,
        sessionID: discoverySession?.sessionID,
        transcriptArtifactID : artifacts?.transcript?.artifactID,
      };
      
      protectedApiCall(
        discoverySession,
        "meta-insight/generate",
        {...apiParams},
        {},
        "post"
      )
        .then(() => {
          client.invalidateQueries({ queryKey: ["artifacts"] });
          client.invalidateQueries({ queryKey: ["insights"] });
          client.invalidateQueries({ queryKey: ["meta-artifacts"] });
          setAction("");
        })
        .finally(() => {
          setIsAlertModalLoading(false);
          setIsAlertModalOpen(false);
        });

    }

    
    const handleConfirmDeleteTranscript = (event, menuProps: any, interviewID: string) => {
        setAlertModalTitle("Delete Transcript");
        setAlertModalDescription("Are you sure you want to remove the transcription and all of its Insights?");
        menuProps.onClose(event);
        setIsAlertModalOpen(true);
        setSelectedPopup("deleteTranscript");
        event.stopPropagation();
    }

    const handleConfirmDeleteInterview = (event, menuProps: any) => {
        setAlertModalTitle("Delete Interview");
        setAlertModalDescription("Are you sure you want to delete this Interview and its Insights?");
        menuProps.close(event);
        setIsAlertModalOpen(true);
        setSelectedPopup("deleteInterview");
        event.stopPropagation();
    }

    const deleteInterview = () => {
        setIsAlertModalLoading(true);
        const interviewID = props?.interviewID;

        setAction("delete");

        handleDeleteInterview({
          sessionID: discoverySession?.sessionID,
          interviewID,
          teamID: base.team.groupID
        })
          .then(async (res) => {
            const interviewData=allInterview?.filter((interview)=>interview?.interviewID!==interviewID);
            setBase(prev => {
                return {
                    ...prev,
                    selectedInterview: getFirstInterview(interviewData, prev.discoveryViewMode)
                }
            })
            setIsAlertModalOpen(false);
          })
          .catch(() => {
            toast.error("failed to delete interview")
          })
          .finally(() => {
            setAction("");
            setIsAlertModalLoading(false);
          });
    }

    const popupOptionMap = {
        "deleteTranscript": handleDeleteTranscript,
        "deleteInterview": deleteInterview,
        "reTranscribe": handleReTranscribe,
        "deleteMedia":handleDeleteMedia,
        "generateAnalysis" : HandleGenerateAnalysis
    }

    return <PopupState variant="popover" popupId="discovery-action-popup">
        {(popupState) => {
            const { onClose, ...otherMenuProps } = bindMenu(popupState);

            const handleMenuClose = (event) => {
                event.stopPropagation();
                onClose();
            }

            const menuProps = {
                onClose: handleMenuClose,
                ...otherMenuProps,
            }

            return (
              <React.Fragment>
                <AlertDialog
                  open={isAlertModalOpen}
                  title={alertModalTitle}
                  selected={selectedPopup}
                  description={alertModalDescription}
                  onClose={() => {
                    setIsAlertModalOpen(false);
                    setAction("");
                  }}
                  component={
                    <Box maxWidth={320} px={3} mb={3}>
                      <DropdownV2
                        placeholder="Select language"
                        selectWillClose={true}
                          onChange={(_, lang) => {
                            setLanguage(lang)
                          }}
                          selected={SUPPORTED_LANGUAGES.find(item => item.key === "English")}
                        items={SUPPORTED_LANGUAGES}
                        theme={theme}
                      />
                    </Box>
                  }
                  confirmButton={
                    <IwButton
                      variant="contained"
                      onClick={popupOptionMap[selectedPopup]}
                      loading={isAlertModalLoading}
                      disabled={isAlertModalLoading}
                    >
                      Confirm
                    </IwButton>
                  }
                  abortButton={
                    <IwButton
                      variant="outlined"
                      // loading={isAlertModalLoading}
                      disabled={isAlertModalLoading}
                      onClick={(event) => {
                        setIsAlertModalOpen(false);
                        setAction("");
                        event.stopPropagation();
                      }}
                    >
                      Cancel
                    </IwButton>
                  }
                />
                {props.renderButton(bindTrigger(popupState))}
                <Menu
                  {...menuProps}
                  sx={{
                    "&.MuiPaper-root": {
                      backgroundColor: "red !important",
                    },
                    ...menuSx
                  }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: menuSide === "left" ? "left" : "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: menuSide === "left" ? "left" : "right",
                  }}
                >
                  <MenuItem
                    sx={optionStyles}
                    onClick={(event) => {
                      if (props.edit) {
                        props.edit();
                      }
                      menuProps.onClose(event);
                    }}
                  >
                    Interview Details
                  </MenuItem>
                  <Divider />
                    <MenuItem
                      sx={optionStyles}
                      onClick={(event) => {
                        handleExportMedia(event, menuProps);
                      }}
                      disabled={!(props?.artifacts?.media || props?.artifacts?.video)}
                    >
                      {action === "exportMedia"
                        ? "Please wait while we process your media"
                        : "Export Media"}
                      {action === "exportMedia" && menuItemLoader}
                    </MenuItem>

                    <MenuItem
                      sx={optionStyles}
                      onClick={(event) => {
                        handleConfirmDeleteMedia(
                          event,
                          menuProps,
                          props?.interviewID
                        );
                      }}
                      disabled={!(props?.artifacts?.media || props?.artifacts?.video)}
                    >
                     Delete Media
                    {action === "delete-media" && menuItemLoader}
                    </MenuItem>

                  <MenuItem
                    sx={optionStyles}
                    onClick={() => {
                      handleExportInterviews();
                    }}
                  >
                    Export Interview
                    {isInterviewDownloadLoading && menuItemLoader}
                  </MenuItem>
                  {canEditCanvas && (
                    <Box>
                      <Divider />
                      <MenuItem
                        sx={optionStyles}
                        onClick={(event) => {
                          handleConfirmDeleteTranscript(
                            event,
                            menuProps,
                            props?.interviewID
                          );
                        }}
                        disabled={!(props?.artifacts?.transcript)}
                      >
                        Delete Transcript
                        {action === "delete-transcript" && menuItemLoader}
                      </MenuItem>
                      <MenuItem
                        sx={optionStyles}
                        onClick={(event) => {
                          handleConfirmReTranscribe(
                            event,
                            menuProps,
                            props?.interviewID
                          );
                        }}
                        disabled={!(props?.artifacts?.media || props?.artifacts?.video || props?.artifacts?.transcript)}
                      >
                        Re-Transcribe Media
                        {action === "transcribe" && menuItemLoader}
                      </MenuItem>
                      {
                        base.team?.isAiInsightGenerationEnabled ? <MenuItem
                        sx={optionStyles}
                        onClick={(event) => {
                          handleGenerateAnalysis(
                            event,
                            menuProps,
                            props?.interviewID
                          );
                        }}
                        disabled={!(props?.artifacts?.media || props?.artifacts?.video || props?.artifacts?.transcript) 
                          || (selectedInterviewDetails?.isMetaInsightGenerated)}
                        >
                          Generate Analysis
                          {action === "generate-analysis" && menuItemLoader}
                        </MenuItem> : null
                      }
                       
                      <Divider />
                      <MenuItem
                        sx={optionStyles}
                        onClick={(event) => {
                          handleConfirmDeleteInterview(event, {
                            ...popupState,
                            close: menuProps.onClose
                          });
                        }}
                      >
                        Delete Interviewee and Data
                        {action === "delete" && menuItemLoader}
                      </MenuItem>
                    </Box>
                  )}
                </Menu>
              </React.Fragment>
            );
        }}
    </PopupState>
}


